import { Scene } from 'phaser';
import { BotaoArcade } from '../components/BotaoArcade';
import { CampoCodigo } from '../components/CampoCodigo';
import { desenharPaginaLima } from '../components/cenario';
import { ModalArcade } from '../components/ModalArcade';
import { criarTextoRico } from '../components/TextoRico';
import { SCENES, TEX } from '../config/constants';
import { ARCADE_HEX, ARCADE_TEXT } from '../config/theme';
import { conferir, deveAbrirInstrucao, distribuirCampos, podeConferir, ProgressoScanPixel } from '../services/scanPixel';
import {
    alfaDosCampos, campoDaPista, chaveDoRotuloDoCampo, encaixarTexto, escalaDoRotuloDaAba, escalaDosCampos, numeroDaAba,
    ordemDasAbas, pistaInicial
} from '../services/scanPixelLayout';
import { Sessao } from '../services/Sessao';
import { t } from '../services/textos';

const VAO_CAMPOS = 16;
const ABAS = { x: 58, y: 248, largura: 1164, altura: 70, vao: 4 };
const AREA_CAMPOS = 1164;   // mesma área útil das abas: x de 58 a 1222
// Texto da pista: começa abaixo das abas e termina antes de "Insira os códigos" (y 497).
const AREA_PISTA = { x: 62, y: 340, largura: 1156, alturaMaxima: 128 };
const TAMANHOS_PISTA = [28, 26, 24, 22, 20];

/**
 * Scan Pixel (Figma, quadros 34–41): cinco enigmas em sequência; cada um
 * tem de uma a dez pistas, alternadas por abas, e um código por pista.
 *
 * A fase é offline-first: o catálogo é baixado uma vez, a conferência é
 * local e o resultado só sobe ao servidor na tela de pontuação.
 */
export class Game extends Scene {
    constructor () {
        super(SCENES.GAME);
    }

    async create () {
        if (!Sessao.ativa) {
            this.scene.start(SCENES.LOGIN);
            return;
        }
        this.fonte = this.registry.get('fonte');
        this.progresso = new ProgressoScanPixel(Sessao.atual.participanteId);
        this.campos = [];
        this.abas = [];
        this.textoPista = [];
        this.modal = null;
        this.travado = false;

        desenharPaginaLima(this, {
            titulo: t('scan.titulo'), rotuloJogador: Sessao.rotulo, rotuloY: 128, espacamento: 0
        });

        let enigmas;
        try {
            enigmas = await this.fonte.obterCatalogoScanPixel();
        } catch (falha) {
            console.error('[ScanPixel] não foi possível carregar os enigmas.', falha);
            if (this.scene.isActive()) this.mostrarFalha();
            return;
        }
        if (!this.scene.isActive()) return;
        this.enigmas = enigmas;

        // Quem já terminou vai direto para a pontuação; a sessão de teste joga de novo.
        if (this.progresso.terminou(enigmas.length)) {
            if (!Sessao.ehTeste) {
                this.scene.start(SCENES.SCAN_RESULTADO);
                return;
            }
            this.progresso.reiniciar();
        }

        this.progresso.iniciar();
        // Presença na etapa: o grupo só finaliza sozinho quando todos os que entraram concluem.
        this.fonte.registrarProgresso?.('scanpixel', this.progresso.estado.concluidos.length);
        this.enigma = enigmas[this.progresso.indiceAtual];
        this.pistaAtual = 0;

        this.desenharCabecalho();
        this.desenharAbas();
        this.desenharCampos();
        // AP9: o que estava nos campos volta (voltar de Materiais, do Aguarde, recarregar).
        // AP12: no enigma embaralhado, abre sempre na primeira aba da esquerda.
        const primeiraPendente = this.restaurarCampos();
        this.selecionarPista(pistaInicial(this.enigma, primeiraPendente), { focar: false });

        // RI-SP-01 / AP8: a instrução vem antes — uma vez — só com a chave do admin ligada.
        // Desligada, o jogador cai direto no enigma (sem foco automático: o teclado cobriria a
        // pista). Quem volta de Materiais ou do Mural também; o "i" ao lado do título reabre.
        if (deveAbrirInstrucao(this.enigma, this.progresso.instrucaoVista(this.enigma.ordem))) {
            this.progresso.marcarInstrucaoVista(this.enigma.ordem);
            this.abrirInstrucao();
        }
    }

    mostrarFalha () {
        this.add.text(640, 400, t('scan.erro_carregar'), ARCADE_TEXT.aviso).setOrigin(0.5);
        new BotaoArcade(this, 640, 500, { rotulo: t('geral.voltar'), aoClicar: () => this.scene.start(SCENES.AGUARDE, {}) });
    }

    // ------------------------------------------------------------- cabeçalho

    desenharCabecalho () {
        // Botão do canto superior esquerdo: leva à página Aguarde.
        this.add.image(64, 48, TEX.BTN_INSIGHTS_REDONDO).setOrigin(0)
            .setInteractive({ useHandCursor: true })
            .on('pointerup', () => this.sairPara(SCENES.AGUARDE));

        const titulo = this.add.text(63, 184, t('scan.enigma', { ordem: this.enigma.ordem, total: this.enigmas.length }), ARCADE_TEXT.tituloFase)
            .setOrigin(0, 0.5);
        this.add.image(titulo.x + titulo.width + 10, 186, TEX.ICON_INFO_PIXEL).setOrigin(0, 0.5);
        // O "i" reabre a instrução; a área de toque cobre título e ícone.
        this.add.zone(63, 187, titulo.width + 48, 48).setOrigin(0, 0.5)
            .setInteractive({ useHandCursor: true })
            .on('pointerup', () => this.abrirInstrucao());

        // RI-SP-09: Materiais abre a página de Materiais.
        this.add.image(1032, 165, TEX.BTN_MATERIAIS_PILULA).setOrigin(0)
            .setInteractive({ useHandCursor: true })
            .on('pointerup', () => this.sairPara(SCENES.MATERIAIS));

        this.add.rectangle(62, 224, 1156, 1, ARCADE_HEX.TINTA).setOrigin(0, 0.5);
        this.add.text(640, 497, t('scan.instrucao_codigos'), ARCADE_TEXT.instrucaoFase).setOrigin(0.5);
    }

    sairPara (cena) {
        if (this.modal || this.travado) return;
        this.scene.start(cena, { voltarPara: SCENES.GAME });
    }

    // ------------------------------------------------------------------ abas

    /**
     * RI-SP-03: uma aba por pista; a largura se ajusta à quantidade.
     * AP12: só as abas embaralham — numeradas PISTA 1..n da esquerda para a direita, cada uma
     * leva ao seu campo, que a linha "RESPOSTA NO CAMPO N" informa; os campos ficam na ordem
     * canônica (a palavra do Enigma 5 se forma nela).
     */
    desenharAbas () {
        const { pistas } = this.enigma;
        const largura = (ABAS.largura - ABAS.vao * (pistas.length - 1)) / pistas.length;

        this.abas = ordemDasAbas(pistas).map(({ pista, indice }, i) => {
            const aba = new BotaoArcade(
                this, ABAS.x + largura / 2 + i * (largura + ABAS.vao), ABAS.y + ABAS.altura / 2, {
                    rotulo: t('scan.pista', { ordem: numeroDaAba(this.enigma, i, pista) }),
                    largura, altura: ABAS.altura,
                    chanfros: { esq: i === 0, dir: i === pistas.length - 1 },
                    aoClicar: () => this.selecionarPista(indice)
                }
            );
            aba.indiceDaPista = indice;
            return aba;
        });
        // O rótulo é sempre "PISTA N": com muitas abas, a fonte diminui por igual em todas.
        const escala = escalaDoRotuloDaAba(this.abas.map((aba) => aba.rotulo.width), largura);
        this.abas.forEach((aba) => aba.rotulo.setScale(escala));
    }

    selecionarPista (indice, { focar = true } = {}) {
        this.pistaAtual = indice;
        this.abas.forEach((aba) => aba.setAlpha(aba.indiceDaPista === indice ? 1 : 0.5));

        this.textoPista.forEach((objeto) => objeto.destroy());
        this.textoPista = this.desenharTextoDaPista(indice);

        this.apontarCampo(indice);
        if (focar && !this.modal) this.campos[indice]?.focar();
    }

    /**
     * AP12 (01/11): no enigma embaralhado, a aba escolhida aponta o campo da resposta — uma seta
     * em cima dele, e os outros campos esmaecidos. Nos outros enigmas, nada muda.
     */
    apontarCampo (indice) {
        this.destaqueDoCampo?.destroy();
        this.destaqueDoCampo = null;
        alfaDosCampos(this.enigma, indice).forEach((alfa, i) => this.campos[i]?.setAlpha(alfa));

        const alvo = campoDaPista(this.enigma, indice) !== null ? this.campos[indice] : null;
        if (!alvo) return;
        const topo = alvo.y - (CampoCodigo.altura() * alvo.scaleY) / 2;
        this.destaqueDoCampo = this.add.graphics()
            .fillStyle(ARCADE_HEX.TINTA, 1)
            .fillTriangle(alvo.x - 13, topo - 22, alvo.x + 13, topo - 22, alvo.x, topo - 6);
    }

    /**
     * Enunciado da pista e, no enigma embaralhado (AP12), a linha "RESPOSTA NO CAMPO N" acima
     * dele. O bloco tem de caber entre as abas e "Insira os códigos": se não couber, a fonte
     * diminui (28 → 20 px).
     */
    desenharTextoDaPista (indice) {
        const { x, y, largura, alturaMaxima } = AREA_PISTA;
        const campo = campoDaPista(this.enigma, indice);
        const montar = (tamanho) => {
            const estilo = { ...ARCADE_TEXT.introducao, fontSize: `${tamanho}px` };
            const entrelinha = Math.round(tamanho * 34 / 28);
            const objetos = [];
            let topo = y;
            if (campo !== null) {
                const aviso = criarTextoRico(this, x, topo, largura, `**${t('scan.resposta_no_campo', { ordem: campo })}**`, estilo, { entrelinha });
                objetos.push(...aviso.objetos);
                topo += aviso.altura + 8;
            }
            const enunciado = criarTextoRico(this, x, topo, largura, this.enigma.pistas[indice].enunciado, estilo, { entrelinha });
            objetos.push(...enunciado.objetos);
            return { objetos, altura: topo + enunciado.altura - y };
        };
        const descartar = ({ objetos }) => objetos.forEach((objeto) => objeto.destroy());
        return encaixarTexto(montar, descartar, TAMANHOS_PISTA, alturaMaxima).objetos;
    }

    // ---------------------------------------------------------------- campos

    desenharCampos () {
        const { pistas } = this.enigma;
        const naturais = pistas.map((pista) => CampoCodigo.larguraPara(pista.tamanho));
        // Com muitas pistas (o conteúdo real chegou a 10) a linha não cabe: reduz campos e vão juntos.
        const escala = escalaDosCampos(naturais, VAO_CAMPOS, AREA_CAMPOS);
        const larguras = naturais.map((l) => l * escala);
        const vao = VAO_CAMPOS * escala;
        const total = larguras.reduce((soma, l) => soma + l, 0) + vao * (pistas.length - 1);
        let x = 640 - total / 2;

        this.campos = pistas.map((pista, i) => {
            const campo = new CampoCodigo(this, x + larguras[i] / 2, 577, {
                tamanho: pista.tamanho,
                // AP12 (01/11): no enigma embaralhado, "CAMPO N"; a aba é que é "PISTA N".
                rotulo: t(chaveDoRotuloDoCampo(this.enigma), { ordem: pista.ordem }),
                aoDigitar: () => {
                    this.atualizarConferir();
                    this.progresso.guardarRascunho(this.enigma, this.valores());
                },
                aoFocar: () => this.pistaAtual !== i && this.selecionarPista(i, { focar: false }),
                aoEnter: () => this.conferir()
            });
            campo.setScale(escala);
            x += larguras[i] + vao;
            return campo;
        });

        this.botaoConferir = new BotaoArcade(this, 640, 728, {
            rotulo: t('geral.conferir'), largura: 318, habilitado: false, variante: 'cinzaApagado',
            aoClicar: () => this.conferir()
        });
    }

    valores () {
        return this.campos.map((campo) => campo.valor);
    }

    /**
     * AP9 (RN-AP-038): certa volta verde e travada; errada, em vermelho e editável; digitada e
     * não conferida, como estava. Devolve o índice da primeira pista que ainda não está certa.
     */
    restaurarCampos () {
        const estados = this.progresso.estadoDasPistas(this.enigma);
        estados.forEach(({ valor, estado }, i) => {
            const campo = this.campos[i];
            if (!campo || !valor) return;
            campo.preencher(valor);
            if (estado) campo.setEstado(estado);
            if (estado === 'ok') campo.bloquear();
        });
        this.atualizarConferir();
        return Math.max(0, estados.findIndex(({ estado }) => estado !== 'ok'));
    }

    /** RI-SP-04. */
    atualizarConferir () {
        this.botaoConferir.setHabilitado(!this.travado && podeConferir(this.enigma.pistas, this.valores()));
    }

    // ------------------------------------------------------------ conferência

    conferir () {
        if (this.travado || this.modal || !podeConferir(this.enigma.pistas, this.valores())) return;

        const resultado = conferir(this.enigma.pistas, this.valores());
        const eUltimo = this.enigma.ordem === this.enigmas.length;
        this.progresso.registrarSubmissao(this.enigma, resultado, eUltimo, this.valores());

        // RI-SP-05 / RI-SP-06: cada campo mostra se o seu código confere.
        resultado.pistas.forEach(({ correta }, i) => {
            this.campos[i].setEstado(correta ? 'ok' : 'erro');
            // RI-SP-07: o código aprovado fica travado; só os errados continuam editáveis.
            if (correta) this.campos[i].bloquear();
        });

        if (!resultado.concluido) {
            this.cameras.main.shake(200, 0.005);
            return;
        }

        // RN-SP-05 / RN-SP-06: enigma concluído libera o material na hora.
        this.travado = true;
        this.botaoConferir.setHabilitado(false);
        this.campos.forEach((campo) => campo.elemento.blur());
        Promise.resolve(this.fonte.concluirEnigma(this.enigma))
            .catch((falha) => console.error('[ScanPixel] falha ao liberar o material.', falha));
        this.time.delayedCall(700, () => this.abrirMaterialLiberado(eUltimo));
    }

    // ------------------------------------------------------------------ modais

    /** O DOM fica acima do canvas: os campos somem enquanto há modal aberto. */
    aoAbrirModal () {
        this.campos.forEach((campo) => campo.setVisivelDom(false));
    }

    aoFecharModal () {
        this.modal = null;
        this.campos.forEach((campo) => campo.setVisivelDom(true));
    }

    abrirInstrucao () {
        if (this.modal || this.travado) return;
        if (!this.enigma.abertura) {
            this.campos[0]?.focar();
            return;
        }

        // Primeira frase em destaque; cada frase seguinte vira um parágrafo.
        const [primeira, ...demais] = this.enigma.abertura.match(/[^.!?]+[.!?]+(\s|$)/g)?.map((f) => f.trim())
            ?? [this.enigma.abertura];

        this.modal = new ModalArcade(this, {
            titulo: t('scan.titulo'),
            blocos: [
                { texto: primeira, peso: '600', maiusculas: true, largura: 600 },
                ...demais.map((texto) => ({ texto }))
            ],
            // RI-SP-02: Iniciar leva ao enigma.
            botao: { rotulo: t('geral.iniciar'), aoClicar: () => this.campos[this.pistaAtual]?.focar() },
            aoAbrir: () => this.aoAbrirModal(),
            aoFechar: () => this.aoFecharModal()
        });
    }

    /** RI-SP-07 / RI-SP-08. */
    abrirMaterialLiberado (eUltimo) {
        const nome = this.enigma.material?.titulo ?? t('scan.material_padrao');

        this.modal = new ModalArcade(this, {
            titulo: t('scan.titulo'),
            largura: 760,
            blocos: [
                { imagem: TEX.LIVRO_MAIS_UM },
                { pixel: t('scan.material_liberado', { nome }) },
                { antes: t('scan.material_onde_antes'), imagem: TEX.BTN_MATERIAIS_MINI, depois: t('scan.material_onde_depois') }
            ],
            botao: {
                rotulo: eUltimo ? t('geral.continuar') : t('scan.proximo_enigma'),
                // RI-SP-10: depois do último enigma vem a página de pontuação.
                aoClicar: () => (eUltimo ? this.scene.start(SCENES.SCAN_RESULTADO) : this.scene.restart())
            },
            aoAbrir: () => this.aoAbrirModal(),
            aoFechar: () => this.aoFecharModal()
        });
    }
}
